import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router';
import { FormikHelpers } from 'formik';
import i18n from '../../utilities/i18n';
import mediator from '../../utilities/mediator';
import { getAuthenticationError } from '../../utilities/auth';
import UpdatePassword, { UpdatePasswordFormValues } from './UpdatePassword';

/**
 * Handles password change when a user's credentials have expired.
 * Reads the user's email from query params, then composes UpdatePassword with the current password field.
 * @returns {React.JSX.Element}
 */
export default function ChangePassword() {
  const location = useLocation();
  const email = location.state?.email || '';
  const [errorMessage, setErrorMessage] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const onSubmit = useCallback(
    (
      values: UpdatePasswordFormValues,
      helpers: FormikHelpers<UpdatePasswordFormValues>,
    ) =>
      mediator
        .request('auth:changePassword', {
          data: {
            currentPassword: values.currentPassword,
            email,
            password: values.password,
          },
        })
        .then(() => {
          setErrorMessage('');
          helpers.resetForm();
          setSuccessModalOpen(true);
        })
        .catch((error) => {
          if (error.status === 403) {
            setErrorMessage(
              i18n.t('Auth_PasswordChange.errorInvalidCurrentPassword'),
            );
          } else if (error.status === 400) {
            setErrorMessage(
              getAuthenticationError(
                error.data,
                'Auth_PasswordChange.errorGeneric',
              ),
            );
          } else {
            setErrorMessage(
              getAuthenticationError(error, 'Auth_PasswordChange.errorGeneric'),
            );
          }
        }),
    [email],
  );

  return (
    <UpdatePassword
      title={i18n.t('Auth_PasswordChange.title')}
      description={i18n.t('Auth_PasswordChange.description')}
      showCurrentPassword
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      successModalOpen={successModalOpen}
    />
  );
}
